import { CSSProperties } from "react";

import CssBaseline from "@mui/material/CssBaseline";
import { Theme as AugmentedTheme } from "@mui/material/styles";
import { createTheme } from "@mui/material/styles";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { ThemeOptions } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { deepmerge } from "@mui/utils";

type Viewport = "xs" | "sm" | "md" | "lg" | "xl";

export const useIsViewportOrLarger = (viewport: Viewport) => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.up(viewport));
};

export const useIsViewportOrSmaller = (viewport: Viewport) => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down(viewport));
};

const getRgbColor = (
  red: number,
  green: number,
  blue: number,
  alpha?: number,
) => {
  if (alpha) {
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  }

  return `rgb(${red}, ${green}, ${blue})`;
};

export const getGrayColor = (val: number, alpha?: number) =>
  getRgbColor(val, val, val, alpha);

const ChipAccentColors = {
  green: getRgbColor(230, 239, 230),
  red: getRgbColor(249, 230, 230),
  gray: getGrayColor(232),
};

// Please define all colors in RGB for consistency.
// NOTE: the last two digits for colors with numeric suffixes represent the
// color's alpha value.
export const Colors = {
  accentGreenSemiOpaque: getRgbColor(46, 125, 50, 0.12),
  accentLightGreenSemiOpaque: getRgbColor(46, 125, 50, 0.04),
  accentLightRedSemiOpaque: getRgbColor(211, 47, 47, 0.04),
  accentRedSemiOpaque: getRgbColor(211, 47, 47, 0.12),
  black: getGrayColor(0),
  blackSemiOpaque: getGrayColor(0, 0.9),
  blue100: getRgbColor(240, 246, 255, 1),
  blue200: getRgbColor(216, 231, 254),
  blue300: getRgbColor(157, 195, 251),
  blue425: getRgbColor(93, 155, 249, 0.25),
  blue500: getRgbColor(38, 122, 247),
  blue700: getRgbColor(5, 78, 189),
  blue704: getRgbColor(5, 78, 189, 0.04),
  blue730: getRgbColor(5, 78, 189, 0.3),
  blue800: getRgbColor(3, 56, 134),
  blue900: getRgbColor(2, 32, 77),
  blue1000: getRgbColor(0, 14, 46),
  blueSemiOpaque: getRgbColor(5, 78, 189, 0.08),
  darkGreen: getRgbColor(27, 94, 32),
  darkGray: getGrayColor(74),
  darkGraySemiOpaque: getGrayColor(0, 0.54),
  darkRed: getRgbColor(211, 47, 47),
  darkRedSemiOpaque: getRgbColor(211, 47, 47, 0.12),
  darkestBlue: getRgbColor(1, 67, 97),
  gray100: getRgbColor(247, 247, 248),
  gray200: getRgbColor(234, 235, 235),
  gray300: getRgbColor(211, 211, 213),
  gray400: getRgbColor(189, 189, 189),
  gray500: getRgbColor(139, 142, 146),
  gray600: getRgbColor(117, 117, 117),
  gray700: getRgbColor(89, 91, 95),
  gray900: getRgbColor(45, 46, 47),
  gray1000: getRgbColor(20, 20, 21),
  green200: getRgbColor(222, 254, 216),
  green300: getRgbColor(171, 251, 157),
  green700: getRgbColor(22, 155, 0, 1),
  green712: getRgbColor(22, 155, 0, 0.12),
  green750: getRgbColor(22, 155, 0, 0.5),
  green800: getRgbColor(16, 112, 0),
  green1000: getRgbColor(5, 36, 0),
  lightBlue: getRgbColor(96, 130, 182),
  lightGreen: getRgbColor(17, 176, 121),
  lightGreenBarelyOpaque: getRgbColor(81, 173, 125, 0.04),
  lightGray: getGrayColor(201),
  lightGraySemiOpaque: getGrayColor(0, 0.12),
  lightestBlue: getRgbColor(229, 246, 253),
  lightestGray: getGrayColor(250),
  lightestGraySemiOpaque: getGrayColor(0, 0.04),
  magenta700: getRgbColor(182, 2, 161),
  magenta800: getRgbColor(138, 0, 122),
  maps: {
    blueRibbon: getRgbColor(37, 98, 225),
    denim: getRgbColor(18, 76, 193),
  },
  mediumBlue: getRgbColor(54, 64, 89),
  mediumGray: getRgbColor(93, 100, 111),
  mediumGraySemiOpaque: getGrayColor(0, 0.23),
  orange200: getRgbColor(254, 230, 216),
  orange300: getRgbColor(251, 193, 157),
  orange400: getRgbColor(249, 150, 93),
  orange500: getRgbColor(247, 115, 38),
  orange600: getRgbColor(228, 85, 5),
  orange700: getRgbColor(182, 68, 2),
  orange800: getRgbColor(138, 51, 0),
  pink400: getRgbColor(249, 93, 150),
  pink600: getRgbColor(224, 6, 85),
  platinum: getGrayColor(232),
  purple400: getRgbColor(171, 93, 249),
  purple500: getRgbColor(143, 38, 247),
  red100: getRgbColor(255, 241, 240),
  red600: getRgbColor(220, 15, 4),
  red700: getRgbColor(182, 11, 2),
  red750: getRgbColor(182, 11, 2, 0.5),
  red712: getRgbColor(182, 11, 2, 0.12),
  red800: getRgbColor(138, 7, 0),
  red1000: getRgbColor(46, 3, 0),
  silver: getGrayColor(196),
  teal600: getRgbColor(6, 202, 224),
  teal800: getRgbColor(0, 110, 122),
  warning: getRgbColor(237, 108, 2),
  whisperGray: getGrayColor(224),
  white: getGrayColor(255),
  white10: getGrayColor(255, 0.1),
  white30: getGrayColor(255, 0.3),
  white40: getGrayColor(255, 0.4),
  whiteSemiOpaque: getGrayColor(255, 0.9),
  yellow200: getRgbColor(254, 250, 216),
  yellow700: getRgbColor(182, 164, 2),
  yellow712: getRgbColor(182, 164, 2, 0.12),
  yellow750: getRgbColor(182, 164, 2, 0.5),
  yellow800: getRgbColor(128, 115, 0),
  yellow1000: getRgbColor(46, 41, 0),
  planning: {
    gray: getGrayColor(67),
  },
} as const;

export const ColorsDark = {
  blue400: getRgbColor(93, 155, 249),
  gray100: getRgbColor(247, 247, 248),
  gray300: getRgbColor(211, 211, 213),
  magenta400: getRgbColor(249, 93, 231),
  orange400: getRgbColor(249, 150, 93),
  pink300: getRgbColor(251, 157, 191),
  teal400: getRgbColor(102, 235, 249),
  teal700: getRgbColor(10, 157, 174),
} as const;

export const getRemSize = (value: number) => `${value}rem`;

// Please define all font sizes in rems for consistency / accessibility.
export const FontSizes = {
  bodyText: getRemSize(0.875),
  display1: getRemSize(2.125),
  display2: getRemSize(1.82),
  h1: getRemSize(2.125),
  h2: getRemSize(1.5),
  h3: getRemSize(1.3),
  h4: getRemSize(1.2),
  h5: getRemSize(1.1),
  h6: getRemSize(0.9),
  large: getRemSize(1.5),
  medium: getRemSize(1),
  small: getRemSize(0.75),
  smallSubHeader: getRemSize(0.7),
} as const;

declare module "@mui/material/styles" {
  interface Theme {
    base: {
      margin: string;
      padding: string;
      radius: string;
    };
    brand: {
      logoUrl: string;
      logoUrlLight: string;
    };
    dialog: {
      titleColor: string;
    };
    header: {
      titleColor: string;
      titleFont: string;
      titleFontWeight: number;
      titleLetterSpacing: string;
    };
    maps: {
      clusterColor: string;
      clusterFont: string;
      mapStyle: {
        dark: string;
        light: string;
      };
    };
    nav: {
      backgroundColor: string;
      borderColor: string;
      color: string;
      iconColor: string;
      selected: {
        backgroundColor: string;
        borderColor: string;
        color: string;
      };
    };
    planning: {
      headerColor?: string;
    };
    tabs: {
      labelColor: string;
      selectedLabelColor: string;
    };
    charts: {
      barGraphDataColors: string[];
      lineGraphDataColors: string[];
    };
  }

  // allow configuration using `createTheme`
  interface ThemeOptions {
    base?: {
      margin?: string;
      padding?: string;
      radius?: string;
    };
    brand?: {
      logoUrl?: string;
      logoUrlLight?: string;
    };
    dialog?: {
      titleColor?: string;
    };
    header?: {
      titleColor?: string;
      titleFont?: string;
      titleFontWeight?: number;
      titleLetterSpacing?: string;
    };
    maps?: {
      clusterColor?: string;
      clusterFont?: string;
      mapStyle?: {
        dark?: string;
        light?: string;
      };
    };
    nav?: {
      backgroundColor?: string;
      borderColor?: string;
      color?: string;
      iconColor?: string;
      logoUrl?: string;
      selected?: {
        backgroundColor?: string;
        borderColor?: string;
        color?: string;
      };
    };
    planning?: {
      headerColor?: string;
    };
    tabs?: {
      labelColor?: string;
      selectedLabelColor?: string;
    };
    charts?: {
      barGraphDataColors: string[];
      lineGraphDataColors: string[];
    };
  }

  /**
   * Adding a custom color to the theme for use in a MUI `color` prop:
   *
   * 1. Ask yourself and your UI/UX designer "Do I really need to add a new
   *    color?". There are a wide variety of existing options available above
   *    in the `Colors` object.
   * 2. Add it to the Palette interface below (pick a good name).
   * 3. Add it to the PaletteOptions interface below (use the name from step 1).
   * 4. Enable it in the module declaration for a given component below (or add
   *    a new one if that component is not already defined).
   * 5. Set the color values as needed in the palette section of the theme below.
   * 6. The component using this new palette color must have an explicit
   *    `<ThemeProvider theme={PlatformTheme}>` directly referencing the
   *    `PlatformTheme` below in order for the color to be usable.
   */

  interface Palette {
    accentGreen: Palette["primary"];
    accentRed: Palette["primary"];
    accentOrange: Palette["primary"];
    black: Palette["primary"];
    background: TypeBackground;
    infoIcon: Palette["primary"];
    white: Palette["primary"];
  }

  // allow configuration using `createTheme`
  interface PaletteOptions {
    accentGreen?: PaletteOptions["primary"];
    accentRed?: PaletteOptions["primary"];
    accentOrange?: PaletteOptions["primary"];
    background?: Partial<TypeBackground>;
    black?: PaletteOptions["primary"];
    infoIcon?: PaletteOptions["primary"];
    mainBackground?: PaletteOptions["primary"];
    white?: PaletteOptions["primary"];
  }

  interface TypeBackground {
    body?: string;
  }

  interface TypographyVariants {
    display1: CSSProperties;
    display2: CSSProperties;
  }

  interface TypographyVariantsOptions {
    display1?: CSSProperties;
    display2?: CSSProperties;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    black: true;
    white: true;
  }
}

declare module "@mui/material/Chip" {
  interface ChipClasses {
    outlinedSuccess: string;
    outlinedError: string;
    filledSuccess: string;
    filledError: string;
  }

  interface ChipPropsVariantOverrides {
    decorative: true;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    display1: true;
    display2: true;
  }
}

const baseTheme = {
  base: {
    margin: "15px",
    padding: "20px",
    radius: "4px",
  },
  brand: {
    logoUrl: "vm_logo_horiz_white.png",
    logoUrlLight: "vm_logo_stacked_orange.png",
  },
  dialog: {
    titleColor: Colors.gray1000,
  },
  header: {
    titleColor: Colors.gray1000,
    titleFont: "Aeonik",
    titleFontWeight: 500,
    titleLetterSpacing: "0.01563rem",
  },
  maps: {
    clusterColor: Colors.orange600,
    clusterFont: "Roboto Mono Bold",
    mapStyle: {
      dark: "mapbox://styles/vistar-media/clkwr6awh00dd01pie60qfr3i",
      light: "mapbox://styles/vistar-media/clsm29t0t03zr01p2h7qfb5f2",
    },
  },
  nav: {
    backgroundColor: Colors.blue900,
    borderColor: getGrayColor(225, 0.3),
    color: Colors.blue200,
    iconColor: Colors.blue200,
    selected: {
      backgroundColor: Colors.blue700,
      borderColor: Colors.blue700,
      color: Colors.white,
    },
  },
  tabs: {
    labelColor: Colors.gray700,
    selectedLabelColor: Colors.gray1000,
  },
  charts: {
    barGraphDataColors: [
      Colors.orange600,
      Colors.orange500,
      Colors.orange400,
      Colors.orange300,
      Colors.orange200,
    ],
    lineGraphDataColors: [Colors.orange700, Colors.orange400],
  },
  palette: {
    divider: Colors.lightGray,
    info: {
      main: Colors.mediumBlue,
      contrastText: Colors.white,
    },
    accentGreen: {
      main: Colors.accentGreenSemiOpaque,
      light: Colors.accentLightGreenSemiOpaque,
    },
    accentRed: {
      main: Colors.accentRedSemiOpaque,
      light: Colors.accentLightRedSemiOpaque,
    },
    accentOrange: {
      main: Colors.orange600,
      light: Colors.orange600,
    },
    primary: {
      main: Colors.blue700,
      contrastText: Colors.white,
    },
    secondary: {
      main: Colors.lightGray,
    },
    text: {
      primary: Colors.gray700,
    },
    background: {
      body: Colors.gray200,
    },
    error: {
      main: Colors.darkRed,
    },
    success: {
      main: Colors.darkGreen,
    },
    infoIcon: {
      main: Colors.lightGray,
    },
    black: {
      main: Colors.black,
    },
    white: {
      main: Colors.white,
    },
    mode: "light",
  },
  planning: {
    headerColor: Colors.blue1000,
  },
  typography: {
    fontSize: 12,
    fontWeightBold: 700,
    fontWeightLight: 300,
    fontWeightMedium: 500,
    fontWeightRegular: 400,
    allVariants: {
      color: Colors.gray700,
    },
    display1: {
      fontFamily: "Aeonik Fono, sans-serif",
      fontSize: FontSizes.display1,
      fontWeight: 400,
      letterSpacing: 0,
      lineHeight: 1.5,
    },
    display2: {
      fontFamily: "Aeonik Fono, sans-serif",
      fontSize: FontSizes.display2,
      fontWeight: 400,
      letterSpacing: 0,
      lineHeight: 1.5,
    },
    h1: {
      color: Colors.blackSemiOpaque,
      fontSize: FontSizes.h1,
      fontWeight: 400,
      lineHeight: 1.5,
    },
    h2: {
      color: Colors.gray700,
      fontSize: FontSizes.h2,
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: FontSizes.h3,
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: FontSizes.h4,
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h5: {
      fontSize: FontSizes.h5,
      fontWeight: 500,
      lineHeight: 1.334,
    },
    h6: {
      fontSize: FontSizes.h6,
      fontWeight: 600,
      lineHeight: 1.334,
    },
  },
  components: {
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          fontSize: FontSizes.bodyText,
          "& .MuiAlert-message": {
            width: "100%",
          },
        },
        standardInfo: {
          backgroundColor: Colors.blue100,
          color: Colors.blue900,
          "& .MuiSvgIcon-root": {
            // severity icon
            color: Colors.blue900,
          },
          "& .MuiButtonBase-root .MuiSvgIcon-root": {
            // close button icon
            color: Colors.blue900,
          },
          "& .MuiTypography-root": {
            // text in the alert
            color: Colors.blue900,
          },
          "& .MuiLink-root:hover": {
            // links in the alert
            color: Colors.blue900,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        colorDefault: {
          backgroundColor: Colors.white,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiChip-root": {
            margin: "0 0 2px 2px",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
        contained: {
          boxShadow: "none",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          "&:hover": {
            borderColor: Colors.blue700,
          },
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          backgroundColor: Colors.gray100,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        outlinedSecondary: {
          color: Colors.blackSemiOpaque,
          backgroundColor: ChipAccentColors.gray,
          border: `1px solid ${Colors.blackSemiOpaque}`,
          "& .MuiChip-icon": {
            color: Colors.darkGray,
          },
        },
        outlinedSuccess: {
          color: Colors.darkGreen,
          backgroundColor: ChipAccentColors.green,
          border: `1px solid ${Colors.darkGreen}`,
          "& .MuiChip-icon": {
            color: Colors.darkGreen,
          },
        },
        outlinedError: {
          color: Colors.darkRed,
          backgroundColor: ChipAccentColors.red,
          border: `1px solid ${Colors.darkRed}`,
          "& .MuiChip-icon": {
            color: Colors.darkRed,
          },
        },
        filledSecondary: {
          color: Colors.blackSemiOpaque,
          backgroundColor: Colors.lightGraySemiOpaque,
          "& .MuiChip-icon": {
            color: Colors.darkGray,
          },
        },
        filledError: {
          color: Colors.darkRed,
          backgroundColor: Colors.accentRedSemiOpaque,
          "& .MuiChip-icon": {
            color: Colors.darkRed,
          },
        },
        filledSuccess: {
          color: Colors.darkGreen,
          backgroundColor: Colors.accentGreenSemiOpaque,
          "& .MuiChip-icon": {
            color: Colors.darkGreen,
          },
        },
        root: {
          display: "flex",
          maxWidth: "min-content",
          padding: "0 8px",
        },
        deleteIcon: {
          order: 1,
          color: Colors.mediumGraySemiOpaque,
          transition: "color 0.1s ease-in-out",
          "&:hover, &:focus": {
            color: Colors.mediumGray,
          },
        },
        label: {
          order: 2,
          padding: 0,
        },
        icon: {
          order: 3,
        },
      },
      variants: [
        {
          props: { variant: "decorative", color: "success" },
          style: {
            backgroundColor: Colors.green700,
          },
        },
        {
          props: { variant: "decorative", color: "warning" },
          style: {
            backgroundColor: Colors.orange600,
          },
        },
        {
          props: { variant: "decorative", color: "error" },
          style: {
            backgroundColor: Colors.red600,
          },
        },
      ],
    },
    MuiCollapse: {
      styleOverrides: {
        root: {
          width: "100%",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: {
          color: Colors.darkRed,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: Colors.blue700,
          textDecoration: "none",
          "&:hover, &:focus": {
            color: Colors.blue700,
            textDecoration: "underline",
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          userSelect: "text",
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: 9,
        },
      },
      variants: [
        {
          props: { size: "small" },
          style: {
            "& input": {
              height: 38,
              width: 38,
            },
            "& .MuiSvgIcon-root": {
              fontSize: 20,
            },
          },
        },
        {
          props: { size: "medium" },
          style: {
            "& input": {
              height: 42,
              width: 42,
            },
            "& .MuiSvgIcon-root": {
              fontSize: 24,
            },
          },
        },
      ],
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          "&.MuiSvgIcon-colorSuccess": {
            color: Colors.green800,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: FontSizes.bodyText,
        },
      },
      variants: [
        {
          props: { variant: "head" },
          style: {
            backgroundColor: Colors.lightestGray,
            borderBottomColor: Colors.lightGraySemiOpaque,
            borderTopColor: Colors.lightGraySemiOpaque,
            borderTopStyle: "solid",
            borderTopWidth: 1,
            fontWeight: 600,
          },
        },
        {
          props: { variant: "body" },
          style: {
            borderBottomColor: Colors.lightGraySemiOpaque,
          },
        },
      ],
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderColor: Colors.blue700,
          color: Colors.blue700,
          textTransform: "none",
          paddingTop: "2px",
          paddingBottom: "2px",
          "&.Mui-selected": {
            backgroundColor: Colors.blue700,
            color: Colors.white,
            "&:hover": {
              backgroundColor: Colors.blue700,
              color: Colors.white,
            },
          },
        },
      },
      variants: [
        {
          props: { color: "error" },
          style: {
            borderColor: Colors.darkRed,
            color: Colors.darkRed,
            "&.Mui-selected": {
              backgroundColor: Colors.darkRedSemiOpaque,
              color: Colors.darkRed,
              "&:hover": {
                backgroundColor: Colors.darkRedSemiOpaque,
                color: Colors.darkRed,
              },
            },
            "&.Mui-disabled": {
              backgroundColor: Colors.darkRedSemiOpaque,
              borderColor: Colors.darkRed,
              "&:hover": {
                backgroundColor: Colors.darkRedSemiOpaque,
                borderColor: Colors.darkRed,
              },
            },
          },
        },
        {
          props: { color: "success" },
          style: {
            borderColor: Colors.darkGreen,
            color: Colors.darkGreen,
            "&.Mui-selected": {
              backgroundColor: Colors.accentGreenSemiOpaque,
              color: Colors.darkGreen,
              "&:hover": {
                backgroundColor: Colors.accentGreenSemiOpaque,
                color: Colors.darkGreen,
              },
            },
            "&.Mui-disabled": {
              backgroundColor: Colors.accentGreenSemiOpaque,
              borderColor: Colors.darkGreen,
              "&:hover": {
                backgroundColor: Colors.accentGreenSemiOpaque,
                borderColor: Colors.darkGreen,
              },
            },
          },
        },
        {
          props: { color: "info" },
          style: {
            "&.Mui-selected": {
              backgroundColor: Colors.blueSemiOpaque,
              "&:hover": {
                backgroundColor: Colors.blueSemiOpaque,
              },
            },
          },
        },
        {
          props: { color: "secondary" },
          style: {
            borderColor: Colors.darkGraySemiOpaque,
            color: Colors.darkGraySemiOpaque,
            "&.Mui-selected": {
              backgroundColor: Colors.white,
              color: Colors.darkGraySemiOpaque,
              "&:hover": {
                backgroundColor: Colors.darkGraySemiOpaque,
                color: Colors.white,
              },
            },
          },
        },
      ],
    },
    MuiTooltip: {
      defaultProps: {
        enterDelay: 0,
        placement: "top",
      },
      styleOverrides: {
        tooltip: {
          fontSize: "12px",
          color: Colors.white,
          backgroundColor: Colors.blackSemiOpaque,
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          display1: "p",
          display2: "p",
        },
      },
    },
  },
  zIndex: {
    mobileStepper: 1000,
    speedDial: 1050,
    appBar: 1100,
    drawer: 1200,
    modal: 2001, // should be greater than 2000, see: vistar_ui_overrides.less
    snackbar: 2101,
    tooltip: 2201,
  },
} satisfies ThemeOptions;

const PlatformTheme = createTheme(baseTheme);

export const BaseFontIncreasePlatformTheme = createTheme(
  deepmerge(baseTheme, {
    typography: {
      allVariants: {
        fontSize: FontSizes.bodyText,
      },
      fontSize: undefined,
    },
  }),
);

interface Nav {
  width?: number;
  closedWidth?: number;
}

// place strict controls on what can and cannot be augmented in a merge
interface AllowedAugmentations {
  sidebar?: Nav;
  menu?: Nav;
}

export const augmentPlatformTheme = (toMerge: AllowedAugmentations) =>
  createTheme(deepmerge(PlatformTheme, toMerge));

export const useTheme = (): AugmentedTheme => useMuiTheme();

export type Theme = AugmentedTheme;

export { CssBaseline };

export default PlatformTheme;
